import { IPhoneEntity } from '../../model/api/phone.entity.interface';
import { IAddressEntity } from '../../model/api/address.entity.interface';
import { ICustomerEntity } from '../../model/api/customer/customer.entity.interface';
import { ICustomerIdentificationEntity } from '../../model/api/customer/customer.identification.entity.interface';
import { IKycStatusEntity } from '../../model/api/customer/kyc-status-entity.interface';
import { IChangePasswordEntity } from '../../model/api/customer/change-password.entity.interface';
import { IChangeEmailEntity } from '../../model/api/customer/change-email.entity.interface';
import { IChangeSecurityQuestionEntity } from '../../model/api/customer/change-security-question.entity.interface';
import { IAdditionalDetailsFields } from '../../model/models/customer/addtionaldetails-fields.interface';
import { ICustomer } from '../../model/models/customer/customer.interface';
import { ICustomerIdentification } from '../../model/models/customer/customer.identification.interface';
import { IKycStatus } from '../../model/models/customer/kyc-status.interface';
import { IPhone } from '../../model/models/phone.interface';
import { IAddress } from '../../model/models/location/address.interface';
import { AddressType } from '../../model/types/address-type';
import { SecurityQuestionType } from '../../model/types/security-question-type';
import { IFormControl } from '../formbuilder/formcontrol.interface';
import { IAdditionalDetailsFieldsEntity } from '../../model/api/customer';
import { USER_FEATURES, USER_LOGINS } from '../myria/app-variables';

export class CustomerMapper {

  static fromCustomerEntity(entity: ICustomerEntity): ICustomer {
    return {
      id: entity.id,
      firstName: entity.firstName,
      middleName: entity.middleName === null ? '' : entity.middleName,
      lastName: entity.lastName,
      lastName2: entity.lastName2,
      stateName: entity.stateName || null,
      address: {
        type: AddressType.Billing.toString(),
        street: entity.address,
        unitNumber: entity.unitNumber || null,
        city: entity.city,
        stateProvince: entity.state,
        country: entity.country,
        postCode: entity.postalCode
      },
      emailAddress: entity.emailAddress,
      homePhone: this.fromPhoneEntity(
        entity.homePhone,
        'homePhone'
      ),
      mobilePhone: this.fromPhoneEntity(
        entity.mobilePhone,
        'mobilePhone'
      ),
      dateOfBirth: entity.dateOfBirth,
      willReceiveEmailMarketing: entity.willReceiveEmailMarketing,
      willReceivePushMarketing: entity.willReceivePushMarketing,
      willReceivePushNotifications: entity.willReceivePushNotifications,
      willReceiveEmailNotifications: entity.willReceiveEmailNotifications,
      willReceiveServiceEmailMessages: entity.willReceiveServiceEmailMessages,
      willReceiveServicePushMessages: entity.willReceiveServicePushMessages,
      adsTracking: entity.adsTracking,
      cultureCode: entity.cultureCode,
      isDisabled: entity.disabled,
      hasTaxId: entity.hasTaxId,
      nationality: entity.nationality,
      countryOfBirth: entity.countryOfBirth,
      occupation: entity.occupation,
      identification: this.fromIdentificationEntity(entity.identification),
      kycStatus: this.fromKycStatusEntity(entity.status),
      businessSectorId : entity.businessSectorId,
      legitimateInterest : entity.legitimateInterest,
      marketingConsentType :entity.marketingConsentType,
      additionalAddresses: entity.additionalAddresses ? entity.additionalAddresses.map(address => this.mapAddress(address, 'from')) : [],
      isAddressSourceLoqate: entity.isAddressSourceLoqate,
      customerForeignIpInformation: entity.customerForeignIpInformation || null,
    };
  }

  static fromPhoneEntity(entity: IPhoneEntity, type: string): IPhone {
    return {
      type: type,
      areaCode: entity.areaCode || null,
      countryCode: entity.countryCode,
      number: entity.number,
      phoneNumberFormatted: entity.phoneNumberFormatted,
      isValid: entity.isValid
    }
  }

  static fromAddressEntity(entity: IAddressEntity, type: string): IAddress {
    return {
      type: type,
      street: entity.address,
      unitNumber: entity.unitNumber,
      city: entity.city,
      stateProvince: entity.state,
      country: entity.country,
      postCode: entity.postalCode
    }
  }

  static fromIdentificationEntity(entity: ICustomerIdentificationEntity): ICustomerIdentification {
    return {
      idVerificationStatus: entity.idVerificationStatus,
      idVerificationAuthority: entity.idVerificationAuthority,
      documentVerificationStatus: entity.documentVerificationStatus,
      documentVerificationAuthority: entity.documentVerificationAuthority,
      manualApprovalFound: entity.manualApprovalFound,
      idVerificationRequired: entity.idVerificationRequired,
      identityVerificationProcessors: entity.identityVerificationProcessors,
      details: entity.details,
      isHighLimitIdentityVerificationRequired: entity.isHighLimitIdentityVerificationRequired,
      isVisaRequired: entity.isVisaRequired,
      visaDetails: entity.visaDetails
    }
  }

  static fromKycStatusEntity(entity: IKycStatusEntity): IKycStatus {
    return {
      canEKyc: entity.canEKyc,
      profileActionsRequired: entity.profileActionsRequired || [],
      canTransact: entity.canTransact,
      kycFailed: entity.kycFailed,
      canAddRecipient: entity.canAddRecipient,
      canEditRecipient: entity.canEditRecipient,
      restrictedCountryTo: entity.restrictedCountryTo

    }
  }

  public static toCustomerEntity(model: ICustomer): ICustomerEntity {
    return {
      id: model.id,
      firstName: model.firstName.trim(),
      middleName: model.middleName.trim() === '' ? null : model.middleName + ' ',
      lastName: model.lastName,
      lastName2: model.lastName2,
      address: model.address.street,
      unitNumber: model.address.unitNumber,
      city: model.address.city,
      state: model.address.stateProvince,
      postalCode: model.address.postCode,
      country: model.address.country,
      emailAddress: model.emailAddress,
      homePhone: {
        areaCode: model.homePhone.areaCode,
        countryCode: model.homePhone.countryCode,
        number: model.homePhone.number,
        phoneNumberNoAreaCode: model.homePhone.number,
        phoneNumberFormatted: model.homePhone.phoneNumberFormatted,
        isValid: model.homePhone.isValid
      },
      mobilePhone: {
        areaCode: model.mobilePhone.areaCode,
        countryCode: model.mobilePhone.countryCode,
        number: model.mobilePhone.number,
        phoneNumberNoAreaCode: model.mobilePhone.number,
        phoneNumberFormatted: model.mobilePhone.phoneNumberFormatted,
        isValid: model.mobilePhone.isValid
      },
      dateOfBirth: model.dateOfBirth,
      securityQuestions: [],
      note: null,
      willReceiveEmailMarketing: model.willReceiveEmailMarketing,
      willReceivePushMarketing: model.willReceivePushMarketing,
      willReceivePushNotifications: model.willReceivePushNotifications,
      willReceiveEmailNotifications: model.willReceiveEmailNotifications,
      willReceiveServiceEmailMessages: model.willReceiveServiceEmailMessages,
      willReceiveServicePushMessages: model.willReceiveServicePushMessages,
      adsTracking: model.adsTracking,
      cultureCode: model.cultureCode,
      disabled: model.isDisabled,
      hasTaxId: model.hasTaxId,
      nationality: model.nationality,
      countryOfBirth: model.countryOfBirth,
      occupationId: null,
      occupation: model.occupation,
      identification: null, // This is really read-only from the API, no need to map back up.
      status: model.kycStatus,
      businessSectorId : model.businessSectorId,
      legitimateInterest : model.legitimateInterest,
      marketingConsentType :model.marketingConsentType,
      validateAdditionalAddress: model.validateAdditionalAddress,
      safeAdditionalAddressOnly: model.safeAdditionalAddressOnly,
      additionalAddresses: model.additionalAddresses.map(address => this.mapAddress(address, 'to')),
      isAddressSourceLoqate: model.isAddressSourceLoqate,
      customerForeignIpInformation: model.customerForeignIpInformation || null,
    };
  }

  static toChangePasswordEntity(model: any): IChangePasswordEntity {
    return {
      currentPassword: model.password,
      newPassword: model.newPassword
    }
  }

  static toChangeEmailEntity(newEmailAddress: string): IChangeEmailEntity {
    return {
      emailAddress: newEmailAddress
    }
  }

  static toChangeSecurityQuestionsEntities(model: any): Array<IChangeSecurityQuestionEntity> {
    return [
      {
        question: model.securityQuestion1,
        answer: model.securityQuestionAnswer1
      },
      {
        question: model.securityQuestion2,
        answer: model.securityQuestionAnswer2
      },
      {
        question: model.securityQuestion3,
        answer: model.securityQuestionAnswer3
      }
    ]
  }

  // Note that we can't use angular's .setValue and .patchValue here because of the way our dynamic forms are constructed.
  // Our custom form object must set the value as it overrides the angular form object.
  static toBillingAddressFormControls(customer: ICustomer, formControls: IFormControl[]): IFormControl[] {
    const newFormControls = [...formControls];

    newFormControls.find(c => c.id === 'firstName').value = customer.firstName;
    newFormControls.find(c => c.id === 'lastName').value = customer.lastName;
    newFormControls.find(c => c.id === 'billingAddress').value = customer.address.street;
    newFormControls.find(c => c.id === 'billingCity').value = customer.address.city;
    newFormControls.find(c => c.id === 'billingState').value = customer.address.stateProvince;
    newFormControls.find(c => c.id === 'billingPostalCode').value = customer.address.postCode;

    return newFormControls;
  }

  static fromSecurityQuestionsEntity(model: any): SecurityQuestionType[] {
    return [ ...model ];
  }

  static toAdditionalFieldsEntity(model: IAdditionalDetailsFields[]) : IAdditionalDetailsFieldsEntity {
    const entity: IAdditionalDetailsFieldsEntity = {
      fields: []
    };
    Object.keys(model).forEach((val) => {
      entity.fields.push({
        name: val,
        defaultValue: model[val]
      });
    });
    return entity;
  }

  static mapAddress(address: any, type: 'to' | 'from'): any {
    return {
      addressId: address.addressId,
      customerId: address.customerId,
      addressTypeId: address.addressTypeId,
      description: address.description,
      unitNumber: address.unitNumber,
      address: type === 'to' ? address.address : undefined,
      city: address.city,
      country: address.country,
      postalCode: type === 'to' ? address.postalCode : undefined,
      state: type === 'to' ? address.state : undefined,
      stateAbbreviation: address.stateAbbreviation,
      street: type === 'from' ? address.address : undefined,
      postCode: type === 'from' ? address.postalCode : undefined,
      stateProvince: type === 'from' ? address.state : undefined,
    };
  }

}



// WEBPACK FOOTER //
// ./src/providers/customer/customer.mapper.ts